import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Award, Users } from "lucide-react";
import { BRAND } from "@/config/brand";
import { TESTIDS } from "@/constants/testIds";

const SLIDES = BRAND.photos;

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      data-testid={TESTIDS.hero.root}
      className="relative min-h-[100svh] w-full overflow-hidden bg-black"
    >
      {/* Background carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="sync">
          {SLIDES.map(
            (src, i) =>
              i === idx && (
                <motion.div
                  key={src}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.6, ease: [0.2, 0.8, 0.2, 1] }}
                  className="absolute inset-0"
                >
                  <img
                    src={src}
                    alt="Vasquez & Son Construction — real project"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              ),
          )}
        </AnimatePresence>
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/70" />
        <div className="absolute inset-0 blueprint-grid opacity-40" />
      </div>

      {/* Spotlight beam */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 1.5 }}
        className="pointer-events-none absolute inset-x-0 top-24 h-[60vh] spotlight-beam animate-pulse-glow"
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-32 pb-24 md:pt-40 md:pb-32">
        {/* Kicker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[color:var(--vs-blue)]" />
          <span className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[color:var(--vs-blue)]">
            Est. Long Island · NY
          </span>
        </motion.div>

        {/* Dynamic Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.6, filter: "blur(20px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ delay: 0.4, duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }}
          data-testid={TESTIDS.hero.logo}
          className="relative mb-10 md:mb-12"
        >
          <div className="relative w-40 md:w-48 lg:w-56">
            <div className="absolute -inset-6 bg-[color:var(--vs-blue)]/20 blur-3xl rounded-full" />
            <img
              src={BRAND.logo}
              alt="Vasquez & Son Construction logo"
              className="relative w-full h-auto object-contain drop-shadow-[0_0_35px_rgba(0,85,255,0.35)]"
            />
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.95] tracking-tight max-w-5xl"
        >
          <span className="chrome-text">Long Island&apos;s</span>
          <br />
          <span className="text-white">Trusted </span>
          <span className="text-[color:var(--vs-blue)]">Home</span>
          <br />
          <span className="text-white">Improvement</span>
          <span className="text-white/40"> / </span>
          <span className="chrome-text">Experts</span>
        </motion.h1>

        {/* Sub */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-gray-300 leading-relaxed"
        >
          Siding · Roofing · Ceramic Floors · Kitchen & Baths · Cement Work ·
          Coping · Power Washing. Family-owned. Fully licensed & insured.
          Serving Nassau and Suffolk counties.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#contact"
            data-testid={TESTIDS.hero.ctaPrimary}
            className="btn-vs btn-primary group"
          >
            Get a Free Quote
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid={TESTIDS.hero.ctaSecondary}
            className="btn-vs btn-secondary"
          >
            <Phone className="w-4 h-4" />
            Call {BRAND.phone}
          </a>
        </motion.div>

        {/* Trust bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.7 }}
          className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl"
        >
          {[
            { icon: ShieldCheck, label: "Licensed & Insured" },
            { icon: Award, label: "20+ Years Experience" },
            { icon: Users, label: "Family Owned & Operated" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-3 border-l-2 border-[color:var(--vs-blue)] pl-4 py-1"
            >
              <Icon className="w-5 h-5 text-[color:var(--vs-blue)] shrink-0" />
              <span className="font-display uppercase tracking-[0.15em] text-[13px] text-white/90">
                {label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-6 right-6 md:right-10 z-10 flex items-center gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            data-testid={`${TESTIDS.hero.slideDot}-${i}`}
            aria-label={`Slide ${i + 1}`}
            className={`h-[2px] transition-all duration-300 ${
              i === idx
                ? "w-10 bg-[color:var(--vs-blue)]"
                : "w-5 bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
